/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMcpApprovalRequestRequest,
  GetMcpApprovalRequestSecurity,
} from "../models/operations/getmcpapprovalrequest.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetMcpApprovalRequestQuery,
  GetMcpApprovalRequestQueryData,
  prefetchGetMcpApprovalRequest,
  queryKeyGetMcpApprovalRequest,
} from "./getMcpApprovalRequest.core.js";
export {
  buildGetMcpApprovalRequestQuery,
  type GetMcpApprovalRequestQueryData,
  prefetchGetMcpApprovalRequest,
  queryKeyGetMcpApprovalRequest,
};

export type GetMcpApprovalRequestQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRequest mcpApproval
 *
 * @remarks
 * Fetch one MCP approval request with its evidence and decision history.
 */
export function useGetMcpApprovalRequest(
  request: GetMcpApprovalRequestRequest,
  security?: GetMcpApprovalRequestSecurity | undefined,
  options?: QueryHookOptions<
    GetMcpApprovalRequestQueryData,
    GetMcpApprovalRequestQueryError
  >,
): UseQueryResult<
  GetMcpApprovalRequestQueryData,
  GetMcpApprovalRequestQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildGetMcpApprovalRequestQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRequest mcpApproval
 *
 * @remarks
 * Fetch one MCP approval request with its evidence and decision history.
 */
export function useGetMcpApprovalRequestSuspense(
  request: GetMcpApprovalRequestRequest,
  security?: GetMcpApprovalRequestSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetMcpApprovalRequestQueryData,
    GetMcpApprovalRequestQueryError
  >,
): UseSuspenseQueryResult<
  GetMcpApprovalRequestQueryData,
  GetMcpApprovalRequestQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetMcpApprovalRequestQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetMcpApprovalRequestData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetMcpApprovalRequestQueryData,
): GetMcpApprovalRequestQueryData | undefined {
  const key = queryKeyGetMcpApprovalRequest(...queryKeyBase);

  return client.setQueryData<GetMcpApprovalRequestQueryData>(key, data);
}

export function invalidateGetMcpApprovalRequest(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpApproval", "getRequest", ...queryKeyBase],
  });
}

export function invalidateAllGetMcpApprovalRequest(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpApproval", "getRequest"],
  });
}
